class GltheaaProductMedia extends HTMLElement {
  static DESKTOP_BREAKPOINT = 990;

  constructor() {
    super();
    this.swiper = null;
    this.thumbsSwiper = null;
    this.mediaQuery = window.matchMedia(`(min-width: ${GltheaaProductMedia.DESKTOP_BREAKPOINT}px)`);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onThumbClick = this.onThumbClick.bind(this);
  }

  get galleryLayout() {
    return this.dataset.galleryLayout === 'thumbnail' ? 'thumbnail' : 'stacked';
  }

  connectedCallback() {
    this.applyColorFilter(this.dataset.selectedColor || '');

    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this.refreshGallery(true), { once: true });
    } else {
      this.refreshGallery(true);
    }

    this.mediaQuery.addEventListener('change', this.onBreakpointChange);
    this.bindThumbs();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.onSectionLoad);
    }
  }

  disconnectedCallback() {
    this.destroy();
    this.destroyThumbs();
    this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
    this.querySelectorAll('[data-thumb-index]').forEach((thumb) => {
      thumb.removeEventListener('click', this.onThumbClick);
    });
    this.variantChangeUnsubscriber?.();

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
    }
  }

  onSectionLoad(event) {
    if (event.detail.sectionId === this.dataset.sectionId) {
      this.sync();
    }
  }

  onBreakpointChange() {
    this.sync();
  }

  onVariantChange(event) {
    if (event.data?.sectionId !== this.dataset.sectionId) return;

    if (this.dataset.filterByColor !== 'true') {
      requestAnimationFrame(() => {
        if (this.swiper) {
          this.swiper.update();
          this.swiper.slideTo(0, 0);
        }
        this.setActiveThumb(0);
      });
      return;
    }

    const nextColor = event.data?.variant?.option1 || '';
    if (this.normalize(nextColor) === this.normalize(this.dataset.selectedColor || '')) return;

    this.applyColorFilter(nextColor);
    requestAnimationFrame(() => this.refreshGallery(true));
  }

  get colorValues() {
    if (this.cachedColorValues) return this.cachedColorValues;

    try {
      const parsed = JSON.parse(this.dataset.colorValues || '[]');
      this.cachedColorValues = Array.isArray(parsed)
        ? parsed.map((value) => this.normalize(value)).filter(Boolean)
        : [];
    } catch (error) {
      this.cachedColorValues = [];
    }

    return this.cachedColorValues;
  }

  normalize(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  isColorTagged(alt) {
    const normalizedAlt = this.normalize(alt);
    if (!normalizedAlt) return false;
    if (this.colorValues.includes(normalizedAlt)) return true;
    return this.colorValues.some((color) => color && normalizedAlt.includes(color));
  }

  mediaGroup(alt) {
    const color = this.normalize(this.dataset.selectedColor || '');
    const normalizedAlt = this.normalize(alt);
    if (!color) return 'match';
    if (!normalizedAlt) return 'shared';
    if (normalizedAlt === color) return 'match';
    if (this.colorValues.includes(normalizedAlt)) return 'hidden';

    const contained = this.colorValues.filter((value) => value && normalizedAlt.includes(value));
    if (contained.length === 0) return 'shared';
    contained.sort((left, right) => right.length - left.length);
    return contained[0] === color ? 'match' : 'hidden';
  }

  applyColorFilter(color) {
    this.dataset.selectedColor = color || '';
    const slides = [...this.querySelectorAll('.gltheaa-product__media-item')];
    const thumbs = [...this.querySelectorAll('.gltheaa-product__thumbs-item')];
    const filtering = this.dataset.filterByColor === 'true' && slides.some((slide) => this.isColorTagged(slide.dataset.mediaAlt));

    const isHidden = (element) => filtering && this.mediaGroup(element.dataset.mediaAlt) === 'hidden';
    const visibleSlides = slides.filter((slide) => !isHidden(slide));
    const showAll = filtering && visibleSlides.length === 0;

    slides.forEach((slide) => this.setSlideVisibility(slide, showAll || !isHidden(slide)));
    thumbs.forEach((thumb) => this.setSlideVisibility(thumb, showAll || !isHidden(thumb)));
    this.reindexThumbs();
  }

  setSlideVisibility(element, visible) {
    element.classList.toggle('is-color-hidden', !visible);
    element.classList.toggle('swiper-slide', visible);
    if (visible) {
      element.removeAttribute('hidden');
    } else {
      element.setAttribute('hidden', '');
    }
  }

  reindexThumbs() {
    let index = 0;
    this.querySelectorAll('.gltheaa-product__thumbs-item').forEach((thumb) => {
      const button = thumb.querySelector('[data-thumb-index]');
      if (!button) return;
      if (thumb.classList.contains('is-color-hidden')) {
        button.dataset.thumbIndex = '-1';
        return;
      }
      button.dataset.thumbIndex = String(index);
      index += 1;
    });
  }

  updateChrome() {
    const visibleCount = this.querySelectorAll('.gltheaa-product__media-item.swiper-slide').length;
    this.dataset.slideCount = String(visibleCount);
    const footer = this.querySelector('.gltheaa-hero-slider__footer');
    if (footer) footer.hidden = visibleCount < 2;
    const thumbs = this.querySelector('[data-product-thumbs]');
    if (thumbs) thumbs.hidden = visibleCount < 2;
  }

  refreshGallery(resetToStart) {
    this.updateChrome();
    this.destroy();
    this.destroyThumbs();
    this.sync();
    if (!resetToStart) return;
    this.swiper?.slideTo(0, 0);
    this.setActiveThumb(0);
  }

  sync() {
    const isDesktop = this.mediaQuery.matches;
    const useSlider = this.galleryLayout === 'thumbnail' || !isDesktop;

    if (!useSlider) {
      this.destroy();
      this.destroyThumbs();
      return;
    }

    this.init();

    if (this.galleryLayout === 'thumbnail' && !isDesktop) {
      this.initThumbs();
    } else {
      this.destroyThumbs();
    }
  }

  init() {
    if (typeof Swiper === 'undefined') return;

    const slideCount = this.querySelectorAll('.gltheaa-product__media-item.swiper-slide').length;
    if (slideCount < 2) {
      this.destroy();
      return;
    }

    this.destroy();

    const swiperElement = this.querySelector('.gltheaa-product__media-track.swiper');
    if (!swiperElement) return;

    const paginationEl = this.querySelector('.gltheaa-hero-slider__pagination');

    const config = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 500,
      loop: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      navigation: {
        nextEl: this.querySelector('.gltheaa-hero-slider__nav--next'),
        prevEl: this.querySelector('.gltheaa-hero-slider__nav--prev'),
      },
      on: {
        slideChange: (swiper) => this.setActiveThumb(swiper.activeIndex),
      },
    };

    if (paginationEl && this.dataset.showPagination === 'true') {
      config.pagination = {
        el: paginationEl,
        clickable: true,
      };
    }

    this.swiper = new Swiper(swiperElement, config);
  }

  initThumbs() {
    if (typeof Swiper === 'undefined') return;

    const thumbsElement = this.querySelector('[data-product-thumbs]');
    if (!thumbsElement) return;

    this.destroyThumbs();

    this.thumbsSwiper = new Swiper(thumbsElement, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
    });
  }

  bindThumbs() {
    this.querySelectorAll('[data-thumb-index]').forEach((thumb) => {
      thumb.addEventListener('click', this.onThumbClick);
    });
  }

  onThumbClick(event) {
    const thumb = event.currentTarget;
    const index = Number(thumb.dataset.thumbIndex || 0);
    if (this.swiper) {
      this.swiper.slideTo(index);
    }
    this.setActiveThumb(index);
  }

  setActiveThumb(index) {
    const thumbs = this.querySelectorAll('[data-thumb-index]');
    thumbs.forEach((thumb) => {
      const isActive = Number(thumb.dataset.thumbIndex) === index;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (!this.thumbsSwiper) return;
    const activeSlide = this.querySelector(`.gltheaa-product__thumbs-item:nth-child(${index + 1})`);
    if (activeSlide) {
      this.thumbsSwiper.slideTo(index);
    }
  }

  destroy() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }

  destroyThumbs() {
    if (!this.thumbsSwiper) return;
    this.thumbsSwiper.destroy(true, true);
    this.thumbsSwiper = null;
  }
}

if (!customElements.get('gltheaa-product-media')) {
  customElements.define('gltheaa-product-media', GltheaaProductMedia);
}
